import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Lock, Unlock, CheckCircle, Star, ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { CJMSClient, cjmsApi } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface LessonUnlockProps {
    client: CJMSClient;
}

interface Lesson {
    id: string;
    title: string;
    duration: string;
    unlocked: boolean;
    completed: boolean;
}

const LESSONS_BY_STAGE: Record<string, Lesson[]> = {
    'Fulfillment': [
        { id: 'f1', title: 'Platform Walkthrough', duration: '15 min', unlocked: true, completed: false },
        { id: 'f2', title: 'First Campaign Setup', duration: '20 min', unlocked: false, completed: false },
        { id: 'f3', title: 'Analytics Fundamentals', duration: '25 min', unlocked: false, completed: false },
    ],
    'Retention': [
        { id: 'r1', title: 'Customer Retention Strategies', duration: '20 min', unlocked: true, completed: false },
        { id: 'r2', title: 'Upsell & Cross-sell Techniques', duration: '25 min', unlocked: false, completed: false },
        { id: 'r3', title: 'Community Building', duration: '30 min', unlocked: false, completed: false },
    ],
    'Growth': [
        { id: 'g1', title: 'Scaling Operations', duration: '25 min', unlocked: true, completed: false },
        { id: 'g2', title: 'Advanced Marketing Automation', duration: '30 min', unlocked: false, completed: false },
        { id: 'g3', title: 'Team Leadership Playbook', duration: '35 min', unlocked: false, completed: false },
    ],
};

export const LessonUnlock: React.FC<LessonUnlockProps> = ({ client }) => {
    const stageLessons = LESSONS_BY_STAGE[client.currentStage] || [];
    const [lessons, setLessons] = useState<Lesson[]>(stageLessons);
    const [expanded, setExpanded] = useState(false);
    const [unlockedCount, setUnlockedCount] = useState(0);

    useEffect(() => {
        setLessons(stageLessons);
        const count = stageLessons.filter(l => l.unlocked).length;
        setUnlockedCount(count);
    }, [client.currentStage]);

    if (stageLessons.length === 0) return null;

    const completedCount = lessons.filter(l => l.completed).length;

    const handleComplete = async (id: string) => {
        setLessons(prev => prev.map(l =>
            l.id === id ? { ...l, completed: !l.completed } : l
        ));

        // Check if this completes all lessons
        const allCompleted = lessons.every(l => l.completed) && lessons.some(l => l.id === id);
        if (allCompleted) {
            feedbackBus.emit('milestone_reached', {
                milestoneId: 'lessons_complete',
                milestoneLabel: 'All Lessons Complete',
                description: 'Completed all lessons for your stage',
            });
        }

        feedbackBus.emit('activity_completed', {
            activityLabel: `Lesson completed: ${lessons.find(l => l.id === id)?.title}`,
            clientEmail: client.email,
        });
    };

    const handleUnlockNext = async () => {
        const nextLocked = lessons.find(l => !l.unlocked);
        if (!nextLocked) return;

        setLessons(prev => prev.map(l =>
            l.id === nextLocked.id ? { ...l, unlocked: true } : l
        ));

        const count = lessons.filter(l => l.unlocked).length;
        setUnlockedCount(count);

        feedbackBus.emit('activity_completed', {
            activityLabel: 'Lesson drip unlocked',
            clientEmail: client.email,
        });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-amber-600" />
                    <h3 className="text-sm font-bold text-slate-800">Lesson Drip Unlock</h3>
                    <span className="text-[10px] text-slate-400 ml-2">
                        {completedCount}/{lessons.length} completed
                    </span>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {expanded && (
                <div className="p-4 pt-0 space-y-2">
                    <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100 mb-3">
                        <Star className="w-3 h-3 text-amber-600" />
                        <p className="text-[10px] text-amber-700">
                            <span className="font-semibold">{unlockedCount}</span> of {lessons.length} lessons unlocked
                        </p>
                    </div>
                    {lessons.map(lesson => (
                        <div
                            key={lesson.id}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                lesson.completed ? 'bg-green-50 border-green-200' :
                                lesson.unlocked ? 'bg-white border-slate-100 hover:border-amber-200' :
                                'bg-slate-50 border-slate-100 opacity-60'
                            }`}
                        >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                {lesson.completed
                                    ? <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                                    : lesson.unlocked
                                        ? <Unlock className="w-4 h-4 text-amber-500 shrink-0" />
                                        : <Lock className="w-4 h-4 text-slate-300 shrink-0" />}
                                <div className="min-w-0">
                                    <span className={`text-xs font-semibold block truncate ${
                                        lesson.completed ? 'text-green-700 line-through' :
                                        lesson.unlocked ? 'text-slate-700' : 'text-slate-400'
                                    }`}>{lesson.title}</span>
                                    <span className="text-[9px] text-slate-400">{lesson.duration}</span>
                                </div>
                            </div>
                            {lesson.unlocked && !lesson.completed && (
                                <button
                                    onClick={() => handleComplete(lesson.id)}
                                    className="px-2 py-1 text-[9px] font-medium bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors shrink-0"
                                >
                                    Complete
                                </button>
                            )}
                        </div>
                    ))}
                    {unlockedCount < lessons.length && (
                        <button
                            onClick={handleUnlockNext}
                            className="w-full mt-2 px-3 py-2 text-[11px] font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                        >
                            Unlock Next Lesson
                        </button>
                    )}
                </div>
            )}

            {/* Support */}
            <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <MessageCircle className="w-3 h-3 text-slate-400" />
                    <p className="text-[9px] text-slate-600">
                        Questions about lessons? WhatsApp +27 81 714 2228 — responded within 6-12 hours.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
